import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { createBoardRoute } from "@air/api/shared-utils";
import { ConfirmationModal } from "@air/component-confirmation-modal";
import intersection from "lodash/intersection";
import Router from "next/router";
import pluralize from "pluralize";
import { useCallback, useState } from "react";
import { useCreateDeleteBoardTask } from "~/components/DeleteBoardTasks/hooks/useCreateDeleteBoardTask";
import { useIsUploadingToSelectedBoard } from "~/components/Modals/DeleteBoards/util";
import { Routes } from "~/constants/routes";
import { useFetchObjectsPermissions } from "~/hooks/useFetchObjectsPermissions";
import { useShowSubscriptionExpiredModal } from "~/hooks/useShowSubscriptionExpiredModal";
import { centralizedBoardSelector } from "~/store/centralizedBoard/selectors";
import { getBoardIdFromPath } from "~/utils/PathUtils";
import { useAirStore } from "~/utils/ReduxUtils";
export const DeleteBoardModal = (param)=>{
    let { onClose, boardIds, onDelete, trackLocation, libraryId } = param;
    const { showingSubscriptionExpiredModal } = useShowSubscriptionExpiredModal({
        onClose
    });
    const { createDeleteBoardTask } = useCreateDeleteBoardTask();
    const { isUploadingToBoard } = useIsUploadingToSelectedBoard(boardIds);
    const [isPending, setIsPending] = useState(false);
    const store = useAirStore();
    const handleCreateDeleteBoardTask = useCallback(async ()=>{
        try {
            var _centralizedBoard_ancestors;
            setIsPending(true);
            await createDeleteBoardTask({
                boardIds,
                libraryId,
                trackLocation
            });
            const centralizedBoard = centralizedBoardSelector(store.getState());
            const centralizedBoardAncestorIds = (centralizedBoard === null || centralizedBoard === void 0 ? void 0 : (_centralizedBoard_ancestors = centralizedBoard.ancestors) === null || _centralizedBoard_ancestors === void 0 ? void 0 : _centralizedBoard_ancestors.map((param)=>{
                let { id } = param;
                return id;
            })) || [];
            const boardIdInUrl = getBoardIdFromPath(window.location.pathname);
            // Check if one of the deleted boards is a board the user is on
            if (boardIdInUrl && boardIds.includes(boardIdInUrl)) {
                // If the board deleted has a parent, send the user to the parent, else home
                Router.push((centralizedBoard === null || centralizedBoard === void 0 ? void 0 : centralizedBoard.parentId) ? createBoardRoute(centralizedBoard.parentId) : Routes.media.all);
            // Redirect if the route is on a child of the deleted board
            } else if (centralizedBoardAncestorIds.length && intersection(centralizedBoardAncestorIds, boardIds).length) {
                Router.push(Routes.media.all);
            }
            onDelete === null || onDelete === void 0 ? void 0 : onDelete();
            onClose();
        } catch (error) {
            console.error("Failed to create delete board task", error);
        }
        setIsPending(false);
    }, [
        boardIds,
        createDeleteBoardTask,
        libraryId,
        onClose,
        onDelete,
        store,
        trackLocation
    ]);
    useFetchObjectsPermissions({
        objects: {
            boardIds
        }
    });
    if (showingSubscriptionExpiredModal) {
        return null;
    }
    return /*#__PURE__*/ _jsxs(ConfirmationModal, {
        ctaPrimaryText: "Delete",
        id: "delete-board",
        isDangerous: true,
        isDisabled: isUploadingToBoard || isPending,
        isLoading: isUploadingToBoard || isPending,
        isOpen: true,
        onConfirm: handleCreateDeleteBoardTask,
        onDismiss: onClose,
        title: "Delete ".concat(pluralize("board", boardIds.length, true), "?"),
        children: [
            isUploadingToBoard && /*#__PURE__*/ _jsx("p", {
                className: "mb-4 font-medium text-red-9",
                children: "Items are currently being uploaded to this board. The board cannot be deleted until this upload has completed."
            }),
            /*#__PURE__*/ _jsxs("ul", {
                className: "mt-2 list-inside list-disc",
                children: [
                    /*#__PURE__*/ _jsxs("li", {
                        className: "mb-2 text-14",
                        children: [
                            "Any sub-boards contained within ",
                            pluralize("these", boardIds.length),
                            " ",
                            pluralize("board", boardIds.length),
                            " ",
                            "will also be deleted."
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("li", {
                        className: "mb-2 text-14",
                        children: [
                            "Assets that live only in ",
                            pluralize("these", boardIds.length),
                            " ",
                            pluralize("board", boardIds.length),
                            " will be deleted."
                        ]
                    })
                ]
            })
        ]
    });
};
